<template>
    <transition name="alert-fade">
        <div 
            v-if="visible" 
            class="v-alert" 
            :class="[`v-alert--${type}`, { 'v-alert--dismissible': dismissible }]"
            role="alert"
        >
            <div class="v-alert__wrapper">
                <div v-if="$slots.prepend || icon" class="v-alert__prepend">
                    <slot name="prepend">
                        <div v-if="icon" class="v-alert__icon">
                            <svg v-if="type === 'success'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                            <svg v-else-if="type === 'error'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                            </svg>
                            <svg v-else-if="type === 'warning'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                            </svg>
                            <svg v-else-if="type === 'info'" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                            </svg>
                        </div>
                    </slot>
                </div>
                
                <div class="v-alert__content">
                    <slot>
                        <div v-if="html" v-html="text"></div>
                        <div v-else>{{ text }}</div>
                    </slot>
                </div>
                
                <div v-if="dismissible" class="v-alert__append">
                    <button class="v-alert__close" @click="close" aria-label="Close">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: "VAlert",
    props: {
        type: {
            type: String,
            default: 'info',
            validator: (value) => ['success', 'info', 'warning', 'error'].includes(value)
        },
        text: {
            type: String,
            default: ''
        },
        dismissible: {
            type: Boolean,
            default: false
        },
        icon: {
            type: Boolean,
            default: true
        },
        html: {
            type: Boolean,
            default: false
        },
        value: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            visible: this.value
        }
    },
    watch: {
        value(newVal) {
            this.visible = newVal;
        },
        visible(newVal) {
            this.$emit('input', newVal);
        }
    },
    methods: {
        close() {
            this.visible = false;
            this.$emit('close');
        }
    }
}
</script>

<style lang="scss" scoped>
@import "/src/scss/assets/variables";

.v-alert {
    position: relative;
    padding: 16px;
    margin-bottom: 16px;
    margin-top: 16px;
    border-radius: 8px;
    border-left: 4px solid;
    font-size: 14px;
    line-height: 1.5;
    
    &__wrapper {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    
    &__prepend {
        flex-shrink: 0;
    }
    
    &__icon {
        width: 20px;
        height: 20px;
        
        svg {
            width: 100%;
            height: 100%;
        }
    }
    
    &__content {
        flex: 1;
        word-wrap: break-word;
    }
    
    &__append {
        flex-shrink: 0;
    }
    
    &__close {
        background: none;
        border: none;
        cursor: pointer;
        padding: 2px;
        border-radius: 4px;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.2s ease;
        
        svg {
            width: 16px;
            height: 16px;
        }
        
        &:hover {
            background-color: rgba(0, 0, 0, 0.1);
        }
    }
    
    // Success style
    &--success {
        background-color: #e8f5e8;
        border-left-color: #4caf50;
        color: #2e7d32;
        
        .v-alert__icon {
            color: #4caf50;
        }
    }
    
    // Error style
    &--error {
        background-color: #ffebee;
        border-left-color: #f44336;
        color: #c62828;
        
        .v-alert__icon {
            color: #f44336;
        }
    }
    
    // Warning style
    &--warning {
        background-color: #fff8e1;
        border-left-color: #ff9800;
        color: #ef6c00;
        
        .v-alert__icon {
            color: #ff9800;
        }
    }
    
    // Info style
    &--info {
        background-color: #e3f2fd;
        border-left-color: $color_blue_cornflower;
        color: #1565c0;
        
        .v-alert__icon {
            color: $color_blue_cornflower;
        }
    }
}

// Animations
.alert-fade-enter-active,
.alert-fade-leave-active {
    transition: all 0.3s ease;
}

.alert-fade-enter {
    opacity: 0;
    transform: translateY(-10px);
}

.alert-fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
